// SCO-650: one delegated listener records every click on a link into the App
// as `signup_cta_clicked`, so no component has to wire its own event. The App
// is served on the same apex host, so its PostHog client continues the same
// anonymous person after consent and then identifies it on sign-in.
//
// Properties are closed: the destination (from a fixed path map), the
// element's own data-cta / data-plan markers (nearest ancestor wins), and the
// current path with its locale prefix kept and the query and hash dropped.
export const SIGNUP_CTA_EVENT = 'signup_cta_clicked';

const DESTINATIONS = Object.freeze({ '/sign-up': 'sign_up', '/sign-in': 'sign_in', '/app': 'dashboard' });
const MARKER = /^[a-z0-9][a-z0-9_.:-]{0,63}$/i;

function marker(element, attribute) {
  const holder = element.closest(`[${attribute}]`);
  const value = holder ? holder.getAttribute(attribute) : null;
  return value && MARKER.test(value) ? value : null;
}

function originOf(value) {
  try { return new URL(value).origin; } catch (_) { return null; }
}

/**
 * The `signup_cta_clicked` properties for a clicked anchor, or null when it
 * does not lead into the App. `location` is the page's window.location;
 * `appOrigin` is the configured VITE_APP_ORIGIN (same-origin always counts).
 */
export function signupCtaProperties(anchor, location, appOrigin) {
  if (!anchor || typeof anchor.getAttribute !== 'function') return null;
  const href = anchor.getAttribute('href');
  if (!href) return null;
  let url;
  try { url = new URL(href, location.href); } catch (_) { return null; }
  if (url.origin !== location.origin && url.origin !== originOf(appOrigin)) return null;
  const destination = DESTINATIONS[url.pathname.replace(/\/+$/, '')];
  if (!destination) return null;
  return {
    destination,
    cta: marker(anchor, 'data-cta'),
    plan: marker(anchor, 'data-plan'),
    page: location.pathname,
  };
}

/**
 * Installs the delegated click listener on `target` (the document). `capture`
 * receives the event name and properties and is responsible for checking that
 * analytics is running. Returns a function that removes the listener.
 */
export function installSignupCtaTracking(target, { location, appOrigin, capture }) {
  const onClick = (event) => {
    try {
      const anchor = event.target && typeof event.target.closest === 'function' ? event.target.closest('a[href]') : null;
      const properties = anchor ? signupCtaProperties(anchor, location, appOrigin) : null;
      if (properties) capture(SIGNUP_CTA_EVENT, properties);
    } catch (_) { /* analytics must never break navigation */ }
  };
  // Capture phase: a component that stops propagation still navigates, so it
  // must still be counted.
  target.addEventListener('click', onClick, true);
  return () => target.removeEventListener('click', onClick, true);
}
